import { PricingApiContext, useProgramMeta } from "@2u-landing-pages/providers";
import { useContext, useEffect, useMemo } from "react";
import { InstallmentPlan, readInstallmentSettings, resolveInstallmentPlan } from "./installments";

type UseInstallmentPlan = {
  /** Null whenever no schedule should render, in which case render nothing. */
  plan: InstallmentPlan | null;
  isLoading: boolean;
};

/**
 * Gives a component the payment schedule the Pricing API supplies for the
 * current program. Prefer this over the {{pricing_api.installments_*}} tokens,
 * which exist for interpolating into authored copy.
 */
export const useInstallmentPlan = (): UseInstallmentPlan => {
  const { pricingApiValues, setCallPricingApi, pricingApiLoading } = useContext(PricingApiContext);
  const programMeta = useProgramMeta();

  const { enabled, calculation } = readInstallmentSettings(programMeta?.fields);

  // The provider holds off on the pricing request until something asks for it,
  // and a switched-off programme has nothing to ask for.
  useEffect(() => {
    if (enabled) setCallPricingApi(true);
  }, [enabled, setCallPricingApi]);

  const plan = useMemo(
    () =>
      resolveInstallmentPlan({
        pricing: pricingApiValues,
        settings: { enabled, calculation },
        tuitionDiscount: programMeta?.fields?.tuition_discount,
      }),
    [pricingApiValues, programMeta, enabled, calculation],
  );

  return {
    plan,
    isLoading: enabled && pricingApiLoading,
  };
};
